// Dialog "Escludi": motivo obbligatorio in textarea (niente window.prompt —
// non da' feedback di busy/errore e blocca l'intera pagina). Stesso pattern
// modale di MarkDialog; verbo distruttivo -> bottone rosso (ReshipConfirmDialog).
import { useEffect, useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { excludeProposal } from "../api";
import type { Proposal } from "../types";

export function ExcludeDialog({
  proposal,
  onDone,
  onClose,
}: {
  proposal: Proposal;
  onDone: () => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ESC per annullare (allineato a CallLogDialog). Ignorato durante l'invio.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const submit = async () => {
    if (!reason.trim()) {
      setError("Il motivo dell'esclusione è obbligatorio.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await excludeProposal(proposal.id, reason.trim());
      onDone();
    } catch (err) {
      setError(prettifyApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Escludi proposta"
      data-no-open
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="w-full max-w-sm rounded-lg border border-line bg-card p-5 shadow-popover">
        <h3 className="font-display text-[16px] font-bold text-ink">
          Escludi — {proposal.customer_name}
        </h3>
        <p className="mt-1 text-[11px] text-ink-2">
          La proposta esce dal flusso di lavorazione. Si può sempre riaprire.
        </p>

        <label className="mt-4 flex flex-col gap-1 text-[12px] font-medium text-ink-2">
          Motivo
          <textarea
            value={reason}
            disabled={busy}
            autoFocus
            rows={3}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Es. duplicato, gia' fatturato manualmente in Eureka…"
            className="resize-y rounded-md border border-line bg-card px-3 py-2 text-[13px] font-normal text-ink outline-none focus:border-accent disabled:opacity-50"
          />
        </label>

        {error && (
          <p role="alert" className="mt-3 text-[12px] text-red">
            {error}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-line bg-card px-3 py-2 text-[12px] font-medium text-ink-2 hover:border-ink-4 disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void submit()}
            className="rounded-md border border-red bg-red px-3 py-2 text-[12px] font-medium text-white disabled:opacity-50"
          >
            {busy ? "Escludo…" : "Escludi"}
          </button>
        </div>
      </div>
    </div>
  );
}
